import React, { Component, Suspense } from 'react';
import {
  oneOf, arrayOf, bool, func, shape, string
} from 'prop-types';
import classNames from 'classnames';
import { Image } from '@thd-olt-component-react/core-ui';
import { Favorite } from '@thd-olt-component-react/save-to-favorites';
import { VideoPlayerProvider, VideoPlayer, pauseAllVideos } from '@thd-olt-component-react/video-player';
import Helper from '../../Helper';
import '../../media-gallery.style.scss';
import './media-gallery-mobile.style.scss';
import { thumbnailClick, mediaClick } from '../../../publisher';

const MediaGalleryOverlayMobile = React.lazy(() => import(
  /* webpackChunkName: "media-gallery-overlay-mobile" */'../MediaGalleryOverlayMobile')
);

class MediaGalleryMobile extends Component {

  constructor(props) {
    super(props);
    this.state = {
      mainImage: null,
      selectedImageIndex: 0,
      overlayOpened: false,
      thumbnails: props?.media?.thumbnails
    };
  }

  componentDidUpdate(prevProps) {
    const { media = {}, product } = this.props;
    const { main = [] } = media;
    if (prevProps?.product.itemId !== product.itemId
      || (prevProps.media && prevProps.media.main[0] !== main[0])) {
      this.updatePageLayout();
    }
  }

  onMainImageClick = (images, index, thumbnailType) => {
    const { thumbnails } = this.state;
    const payload = {
      eventName: 'expanded-view',
      primaryCategory: 'overlay'
    };
    thumbnailClick(payload);

    const thumbnailPayload = {
      section: 'media',
      component: 'thumbnails',
      target: thumbnailType || 'n/a',
      position: 'n/a'
    };

    mediaClick(thumbnailPayload);

    this.setState({
      overlayOpened: true, mainImage: images[index], selectedImageIndex: index, thumbnails
    });
    if (typeof window !== 'undefined') {
      window.history.pushState({ page: 'MEDIAOVERLAY' }, null, '');
    }
  };

  setChildrenElements = (images) => {
    const {
      product, overrideData, showFavorites, firstItemFetchPriority, autoPlayInViewportEnabled
    } = this.props;
    const brandName = overrideData?.brandNameLabel || product.identifiers?.brandName;
    const galleryLabel = overrideData?.galleryLabel || product.identifiers?.productLabel;
    const children = (images || []).map((image, imageIndex) => {
      const {
        collection, collectionId, mediaStill, url, videoStill, type, altText
      } = image;
      const isImage360 = Helper.isImage360({ media: image });
      const isVideo = Helper.isVideo({ media: image });
      const isAR = Helper.isAR({ media: image });
      const isHotspot = Helper.isHotspot({ media: image });
      const { source, videoSource, videoId } = Helper.formatVideoProps(image);
      const alt = altText || `${brandName} ${galleryLabel}`;

      const lazy = imageIndex > 1;
      let thumbnailUrl = image?.url;
      if (isVideo) {
        thumbnailUrl = videoStill;
      } else if (isImage360) {
        thumbnailUrl = `${url}?thumb&profile=400`;
      } else if (isAR) {
        thumbnailUrl = mediaStill;
      }

      return (
        <li className="media-gallery-mobile" key={imageIndex}>
          {showFavorites && (
            <Favorite
              className="media-gallery-favorite"
              collectionId={collectionId}
              collectionImage={url}
              position="topRight"
              showCircle
            />
          )}
          {(autoPlayInViewportEnabled && isVideo)
            ? <VideoPlayer videoId={videoId} source={source} videoSrc={videoSource} />
            : (
              <button
                className="media-gallery-mobile__thumbnails-wrapper"
                onClick={() => this.onMainImageClick(images, imageIndex, type)}
                type="button"
              >
                {(isImage360 || isVideo || isAR || isHotspot)
                    && (<span className="background-circle background-circle--main" />)}
                {isImage360 && (<span className="threesixty-icon threesixty-icon--main" />)}
                {isVideo && (<span className="play-icon play-icon--main" />)}
                {isAR && (<span className="ar-icon ar-icon--main" />)}
                {isHotspot && (<span className="hotspot-tag-icon hotspot-tag-icon__main" />)}
                <Image
                  className="media-gallery-mobile__thumbnails"
                  src={thumbnailUrl}
                  alt={alt}
                  lazy={lazy}
                  height="263"
                  width="263"
                  fetchpriority={imageIndex === 0 ? firstItemFetchPriority : null}
                />
                {collection
                    && (
                      <div className="shopThisRoomContainer">
                        <a
                          className="shopThisRoomLink"
                          href={collection}
                          onClick={(e) => e && e.stopPropagation()}
                        > Shop This Room
                        </a>
                      </div>
                    )}
              </button>
            )}
        </li>
      );
    });
    return children;
  };

  handleMediaClick = ({ media }) => {
    const { onMediaClick } = this.props;
    if (onMediaClick) {
      onMediaClick({ media });
    }
  };

  updatePageLayout = () => {
    const { media } = this.props;
    const { main, thumbnails } = media;
    this.setState({
      mainImage: main[0],
      selectedImageIndex: 0,
      thumbnails
    });
    this.setChildrenElements(main);
  };

  hideOverlay = () => {
    this.setState({ overlayOpened: false });
  };

  render() {
    const {
      overlayOpened, thumbnails, mainImage, selectedImageIndex
    } = this.state;
    const { product, media, overrideData, autoPlayInViewportEnabled } = this.props;
    const { main = [], zoom = [] } = media;
    const wrapperClass = classNames(
      'media-gallery-mobile__wrapper',
      { 'single-image': main.length === 1 }
    );

    if (autoPlayInViewportEnabled && overlayOpened) {
      pauseAllVideos();
    }

    return (
      <>
        {autoPlayInViewportEnabled
          ? (
            <VideoPlayerProvider autoPlayInViewport>
              <ul data-component="MediaGallery" className={wrapperClass}>
                {this.setChildrenElements(main)}
              </ul>
            </VideoPlayerProvider>
          )
          : (
            <ul data-component="MediaGallery" className={wrapperClass}>
              {this.setChildrenElements(main)}
            </ul>
          )}
        {overlayOpened
          && (
            <Suspense fallback={<div />}>
              <MediaGalleryOverlayMobile
                handleClose={this.hideOverlay}
                overlayOpened={overlayOpened}
                product={product}
                mainImage={mainImage}
                mainImages={main}
                overrideData={overrideData}
                onMediaClick={this.handleMediaClick}
                zoomedImages={zoom}
                thumbnails={thumbnails}
                selectedImageIndex={selectedImageIndex}
              />
            </Suspense>
          )}
      </>
    );
  }
}

MediaGalleryMobile.propTypes = {
  media: shape({
    main: arrayOf(shape({
      id: string,
      location: string,
      mediaType: string,
      altText: string
    })),
    thumbnails: arrayOf(shape({
      id: string,
      location: string,
      mediaType: string,
      altText: string
    })),
    zoom: arrayOf(shape({
      id: string,
      location: string,
      mediaType: string,
      altText: string
    }))
  }).isRequired,
  overrideData: shape({
    galleryLabel: string,
    brandNameLabel: string
  }),
  onMediaClick: func,
  product: shape({}),
  showFavorites: bool,
  firstItemFetchPriority: oneOf([
    'high', // Fetch the image at a high priority relative to other images.
    'low', // Fetch the image at a low priority relative to other images.
    'auto' // Indicates no preference for the fetch priority. The browser decides what is best for the user.
  ]),
  autoPlayInViewportEnabled: bool
};

MediaGalleryMobile.defaultProps = {
  onMediaClick: null,
  product: {},
  showFavorites: false,
  overrideData: null,
  firstItemFetchPriority: 'auto',
  autoPlayInViewportEnabled: false
};

export { MediaGalleryMobile };
